/**
 * Sample trip presets used by the onboarding picker and the Profile manager.
 *
 * Photos are sourced from Wikimedia Commons under PD / CC0 / CC-BY / CC-BY-SA
 * licenses. The asset pointers live under src/assets/sample-trips/<preset>/.
 *
 * The DB seed function (seed_sample_trip) stores each photo's storage_path as
 * "sample://<preset>/<file>". Use resolveSampleMediaUrl() to map that back to
 * the CDN URL at render time.
 *
 * Each photo carries the per-stop metadata that seed_sample_trip mirrors into
 * media_assets: lat/lng, altitude_meters, heading_degrees, captured_at,
 * uploaded_at, highlight flag, ai_tags, plus full attribution. The richer
 * narrative fields (subtitle/description/days/highlights and per-photo
 * caption) live in this module only and are surfaced by the onboarding
 * picker and library preview — no DB schema change.
 *
 * IMPORTANT: this module must stay off the launch / tracking / widget /
 * Control Center paths. Only onboarding (SampleTripPicker / SampleTripCard),
 * the library preview, and the media URL resolver should import it.
 */

import alps1 from "@/assets/sample-trips/alps/alps-1.jpg.asset.json";
import alps2 from "@/assets/sample-trips/alps/alps-2.jpg.asset.json";
import alps3 from "@/assets/sample-trips/alps/alps-3.jpg.asset.json";
import alps4 from "@/assets/sample-trips/alps/alps-4.jpg.asset.json";
import alps5 from "@/assets/sample-trips/alps/alps-5.jpg.asset.json";
import alps6 from "@/assets/sample-trips/alps/alps-6.jpg.asset.json";
import amz1 from "@/assets/sample-trips/amazon/amazon-1.jpg.asset.json";
import amz2 from "@/assets/sample-trips/amazon/amazon-2.jpg.asset.json";
import amz3 from "@/assets/sample-trips/amazon/amazon-3.jpg.asset.json";
import amz4 from "@/assets/sample-trips/amazon/amazon-4.jpg.asset.json";
import amz5 from "@/assets/sample-trips/amazon/amazon-5.jpg.asset.json";
import amz6 from "@/assets/sample-trips/amazon/amazon-6.jpg.asset.json";
import rk1 from "@/assets/sample-trips/rockies/rockies-1.jpg.asset.json";
import rk2 from "@/assets/sample-trips/rockies/rockies-2.jpg.asset.json";
import rk3 from "@/assets/sample-trips/rockies/rockies-3.jpg.asset.json";
import rk4 from "@/assets/sample-trips/rockies/rockies-4.jpg.asset.json";
import rk5 from "@/assets/sample-trips/rockies/rockies-5.jpg.asset.json";
import rk6 from "@/assets/sample-trips/rockies/rockies-6.jpg.asset.json";
import jp1 from "@/assets/sample-trips/japan/japan-1.jpg.asset.json";
import jp2 from "@/assets/sample-trips/japan/japan-2.jpg.asset.json";
import jp3 from "@/assets/sample-trips/japan/japan-3.jpg.asset.json";
import jp4 from "@/assets/sample-trips/japan/japan-4.jpg.asset.json";
import jp5 from "@/assets/sample-trips/japan/japan-5.jpg.asset.json";
import jp6 from "@/assets/sample-trips/japan/japan-6.jpg.asset.json";

export type SamplePresetKey = "alps" | "amazon" | "rockies" | "japan";

export const SAMPLE_PRESET_KEYS: readonly SamplePresetKey[] = [
  "alps",
  "amazon",
  "rockies",
  "japan",
] as const;

export type SamplePhoto = {
  file: string;
  url: string;
  // Attribution
  title: string;
  /** Richer one-line caption surfaced as ai_caption in media_assets. */
  caption: string;
  author: string;
  license: string;
  licenseUrl: string;
  sourceUrl: string;
  attributionText: string;
  attributionRequired: boolean;
  // Per-stop metadata (mirrored into media_assets by seed_sample_trip)
  lat: number;
  lng: number;
  altitude_meters: number;
  heading_degrees: number;
  captured_at: string; // ISO 8601 with offset
  uploaded_at: string; // ISO 8601 with offset
  highlight: boolean;
  ai_tags: string[];
};

export type SampleDay = {
  day: number;
  title: string;
  summary: string;
};

export type SamplePreset = {
  key: SamplePresetKey;
  name: string;
  shortName: string;
  /** Short tagline already shown on the picker card. */
  tagline: string;
  /** New: one-line preview subtitle (richer than tagline, shorter than description). */
  subtitle: string;
  /** New: 2–3 sentence narrative used in the expanded picker / library preview. */
  description: string;
  destination: string;
  heroUrl: string;
  /** Local IANA timezone used by seed_sample_trip when inserting points. */
  timezone: string;
  /** Trip date window (YYYY-MM-DD). */
  dateStart: string;
  dateEnd: string;
  /** Realistic route ping series for location_points. */
  route: SampleRoutePoint[];
  photos: SamplePhoto[];
  /** New: short day-by-day list for the preview UI. */
  days: SampleDay[];
  /** New: 3–6 trip-level highlight phrases for the preview UI. */
  highlights: string[];
};

export type SampleRoutePoint = {
  recorded_at: string;
  lat: number;
  lng: number;
  altitude_meters: number;
  heading_degrees: number;
  speed_mps: number;
  accuracy_meters: number;
  label: string;
};

/* ============================================================
 * ALPS — Italian Alps / Dolomites trek
 * ============================================================ */

const ALPS_ROUTE: SampleRoutePoint[] = [
  { recorded_at: "2026-06-06T08:30:00+02:00", lat: 46.5405, lng: 12.1330, altitude_meters: 1224, heading_degrees: 28,  speed_mps: 1.2, accuracy_meters: 8,  label: "Cortina d'Ampezzo — arrival" },
  { recorded_at: "2026-06-06T10:05:00+02:00", lat: 46.6940, lng: 12.0859, altitude_meters: 1496, heading_degrees: 64,  speed_mps: 0.9, accuracy_meters: 7,  label: "Lago di Braies" },
  { recorded_at: "2026-06-06T15:20:00+02:00", lat: 46.6123, lng: 12.2956, altitude_meters: 1752, heading_degrees: 95,  speed_mps: 7.0, accuracy_meters: 11, label: "Misurina road" },
  { recorded_at: "2026-06-07T08:40:00+02:00", lat: 46.6187, lng: 12.3028, altitude_meters: 1754, heading_degrees: 42,  speed_mps: 1.0, accuracy_meters: 8,  label: "Rifugio Auronzo / Tre Cime trailhead" },
  { recorded_at: "2026-06-07T10:30:00+02:00", lat: 46.6367, lng: 12.3109, altitude_meters: 2320, heading_degrees: 345, speed_mps: 0.8, accuracy_meters: 9,  label: "Lavaredo loop — towers" },
  { recorded_at: "2026-06-07T13:15:00+02:00", lat: 46.6180, lng: 12.2955, altitude_meters: 2210, heading_degrees: 210, speed_mps: 0.7, accuracy_meters: 9,  label: "Cadini di Misurina viewpoint" },
  { recorded_at: "2026-06-08T09:50:00+02:00", lat: 46.5570, lng: 11.7205, altitude_meters: 2450, heading_degrees: 75,  speed_mps: 0.8, accuracy_meters: 8,  label: "Seceda / Val Gardena ridge" },
  { recorded_at: "2026-06-08T13:05:00+02:00", lat: 46.5415, lng: 11.6175, altitude_meters: 1840, heading_degrees: 135, speed_mps: 1.0, accuracy_meters: 8,  label: "Rifugio dinner / sunset" },
];

const ALPS: SamplePhoto[] = [
  { file: "alps-1.jpg", url: alps1.url,
    title: "Cortina Arrival",
    caption: "Mountain town arrival with peaks above — first afternoon in Cortina.",
    author: "_大面",
    license: "CC BY 2.5", licenseUrl: "https://creativecommons.org/licenses/by/2.5",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Tre_Cime_di_Lavaredo_in_summer.jpg",
    attributionText: '"Tre Cime di Lavaredo" by _大面, CC BY 2.5 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 46.5405, lng: 12.1330, altitude_meters: 1224, heading_degrees: 28,
    captured_at: "2026-06-06T08:35:12+02:00", uploaded_at: "2026-06-06T08:58:12+02:00",
    highlight: false, ai_tags: ["sample", "dolomites", "cortina", "town", "mountains", "arrival"] },
  { file: "alps-2.jpg", url: alps2.url,
    title: "Alpine Lake Stop",
    caption: "Blue water surrounded by steep rock — short stop at the alpine lake.",
    author: "Kufoleto",
    license: "CC BY 3.0", licenseUrl: "https://creativecommons.org/licenses/by/3.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Lago_di_Sorapiss.jpg",
    attributionText: '"Lago di Sorapis" by Kufoleto, CC BY 3.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 46.6940, lng: 12.0859, altitude_meters: 1496, heading_degrees: 64,
    captured_at: "2026-06-06T10:08:33+02:00", uploaded_at: "2026-06-06T10:31:33+02:00",
    highlight: true, ai_tags: ["sample", "dolomites", "lake", "alpine", "rest", "highlight"] },
  { file: "alps-3.jpg", url: alps3.url,
    title: "Tre Cime Trailhead",
    caption: "Starting the loop beneath the towers — early morning at Rifugio Auronzo.",
    author: "creator-bz",
    license: "Public domain", licenseUrl: "https://en.wikipedia.org/wiki/Public_domain",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Pragser_Wildsee",
    attributionText: '"Lago di Braies" by creator-bz, Public domain (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 46.6187, lng: 12.3028, altitude_meters: 1754, heading_degrees: 42,
    captured_at: "2026-06-07T08:43:18+02:00", uploaded_at: "2026-06-07T09:05:18+02:00",
    highlight: false, ai_tags: ["sample", "dolomites", "trailhead", "alpine", "hike", "morning"] },
  { file: "alps-4.jpg", url: alps4.url,
    title: "Lavaredo Towers",
    caption: "Jagged peaks close above the path — the towers from the Lavaredo loop.",
    author: "Wikimedia Commons contributor (own work)",
    license: "CC BY-SA 3.0", licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Drei_Zinnen_Tre_Cime_di_Lavaredo_Dolomites.jpg",
    attributionText: '"Drei Zinnen / Tre Cime di Lavaredo, Dolomites", CC BY-SA 3.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 46.6367, lng: 12.3109, altitude_meters: 2320, heading_degrees: 345,
    captured_at: "2026-06-07T10:35:09+02:00", uploaded_at: "2026-06-07T10:58:09+02:00",
    highlight: true, ai_tags: ["sample", "dolomites", "tre cime", "peaks", "hike", "highlight"] },
  { file: "alps-5.jpg", url: alps5.url,
    title: "Cadini Viewpoint",
    caption: "Narrow ridge and dramatic skyline — the classic Cadini di Misurina overlook.",
    author: "Antonio De Lorenzo",
    license: "CC BY 2.5", licenseUrl: "https://creativecommons.org/licenses/by/2.5",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Rifugio_Auronzo",
    attributionText: '"Rifugio Auronzo" by Antonio De Lorenzo, CC BY 2.5 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 46.6180, lng: 12.2955, altitude_meters: 2210, heading_degrees: 210,
    captured_at: "2026-06-07T13:18:40+02:00", uploaded_at: "2026-06-07T13:43:40+02:00",
    highlight: true, ai_tags: ["sample", "dolomites", "viewpoint", "ridge", "dramatic", "highlight"] },
  { file: "alps-6.jpg", url: alps6.url,
    title: "Rifugio Sunset",
    caption: "Golden light after a long hiking day — sunset from the hut.",
    author: "Szydzio",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Auronzo_di_Cadore",
    attributionText: '"Auronzo di Cadore" by Szydzio, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 46.5415, lng: 11.6175, altitude_meters: 1840, heading_degrees: 135,
    captured_at: "2026-06-08T13:09:27+02:00", uploaded_at: "2026-06-08T13:32:27+02:00",
    highlight: true, ai_tags: ["sample", "dolomites", "hut", "sunset", "recovery", "highlight"] },
];

const ALPS_DAYS: SampleDay[] = [
  { day: 1, title: "Arrival in Cortina", summary: "Settle into the mountain town, evening walk under the peaks." },
  { day: 2, title: "Tre Cime loop", summary: "Trailhead at Rifugio Auronzo, the towers, and back via Cadini viewpoint." },
  { day: 3, title: "Lake and ridge viewpoints", summary: "Lago di Braies in the morning, Seceda ridge in the afternoon." },
  { day: 4, title: "Hut-to-hut style hiking", summary: "Longer alpine traverse with a refuge stop for lunch." },
  { day: 5, title: "Return and final overlook", summary: "Slow descent, one last viewpoint before the drive out." },
];

const ALPS_HIGHLIGHTS = [
  "Tre Cime di Lavaredo loop",
  "Cadini di Misurina viewpoint",
  "Lago di Braies at sunrise",
  "Seceda / Val Gardena ridge",
  "Rifugio sunset dinner",
];

/* ============================================================
 * AMAZON — Brazilian rainforest
 * ============================================================ */

const AMAZON_ROUTE: SampleRoutePoint[] = [
  { recorded_at: "2026-06-06T09:40:00-04:00", lat: -3.1190, lng: -60.0217, altitude_meters: 35, heading_degrees: 82,  speed_mps: 1.2, accuracy_meters: 10, label: "Manaus port — departure" },
  { recorded_at: "2026-06-06T10:25:00-04:00", lat: -3.1370, lng: -59.9000, altitude_meters: 28, heading_degrees: 105, speed_mps: 8.0, accuracy_meters: 18, label: "Rio Negro — by boat" },
  { recorded_at: "2026-06-06T12:20:00-04:00", lat: -3.1345, lng: -59.8792, altitude_meters: 25, heading_degrees: 120, speed_mps: 7.0, accuracy_meters: 18, label: "Meeting of Waters" },
  { recorded_at: "2026-06-07T08:30:00-04:00", lat: -3.2050, lng: -60.0228, altitude_meters: 31, heading_degrees: 165, speed_mps: 2.5, accuracy_meters: 15, label: "Flooded forest — canoe" },
  { recorded_at: "2026-06-07T10:45:00-04:00", lat: -3.2158, lng: -60.0472, altitude_meters: 30, heading_degrees: 240, speed_mps: 0.9, accuracy_meters: 12, label: "Rainforest boardwalk" },
  { recorded_at: "2026-06-07T16:30:00-04:00", lat: -3.1690, lng: -60.1040, altitude_meters: 29, heading_degrees: 310, speed_mps: 6.8, accuracy_meters: 20, label: "Return by river — sunset" },
  { recorded_at: "2026-06-08T09:15:00-04:00", lat: -3.1008, lng: -60.0253, altitude_meters: 40, heading_degrees: 15,  speed_mps: 1.1, accuracy_meters: 9,  label: "Manaus — morning return" },
];

const AMAZON: SamplePhoto[] = [
  { file: "amazon-1.jpg", url: amz1.url,
    title: "Leaving Manaus by Boat",
    caption: "City edge giving way to open river — pushing off from Manaus port.",
    author: "Ridiculopathy",
    license: "CC0", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Teatro_Amazonas",
    attributionText: '"Theatro Amazonas" by Ridiculopathy, CC0 (via Wikimedia Commons)',
    attributionRequired: false,
    lat: -3.1190, lng: -60.0217, altitude_meters: 35, heading_degrees: 82,
    captured_at: "2026-06-06T09:48:16-04:00", uploaded_at: "2026-06-06T10:06:16-04:00",
    highlight: false, ai_tags: ["sample", "amazon", "manaus", "boat", "departure", "river-route"] },
  { file: "amazon-2.jpg", url: amz2.url,
    title: "Rio Negro Waterline",
    caption: "Dark water and wide sky from the boat — long stretch on the Rio Negro.",
    author: "James Martins",
    license: "CC BY 3.0", licenseUrl: "https://creativecommons.org/licenses/by/3.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Meeting_of_Waters",
    attributionText: '"Encontro das Águas" by James Martins, CC BY 3.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: -3.1370, lng: -59.9000, altitude_meters: 28, heading_degrees: 105,
    captured_at: "2026-06-06T10:34:22-04:00", uploaded_at: "2026-06-06T10:55:22-04:00",
    highlight: true, ai_tags: ["sample", "amazon", "rio negro", "river", "rainforest", "river-route", "highlight"] },
  { file: "amazon-3.jpg", url: amz3.url,
    title: "Rainforest Trail",
    caption: "Humid green trail under dense canopy — boardwalk into the rainforest.",
    author: "Google Earth Timelapse (Google, Landsat, Copernicus)",
    license: "CC BY 2.5", licenseUrl: "https://creativecommons.org/licenses/by/2.5",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Amazon_rainforest",
    attributionText: '"Amazon rainforest" by Google Earth Timelapse, CC BY 2.5 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: -3.1345, lng: -59.8792, altitude_meters: 25, heading_degrees: 120,
    captured_at: "2026-06-06T12:24:38-04:00", uploaded_at: "2026-06-06T12:49:38-04:00",
    highlight: false, ai_tags: ["sample", "amazon", "rainforest", "trail", "canopy", "offline-friendly"] },
  { file: "amazon-4.jpg", url: amz4.url,
    title: "Canoe Through Flooded Forest",
    caption: "Quiet water path between trees — paddling through the flooded forest.",
    author: "Jymlii Manzo",
    license: "CC BY 2.0", licenseUrl: "https://creativecommons.org/licenses/by/2.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Rio_Negro_(Amazon)",
    attributionText: '"Rio Negro" by Jymlii Manzo, CC BY 2.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: -3.2050, lng: -60.0228, altitude_meters: 31, heading_degrees: 165,
    captured_at: "2026-06-07T08:36:12-04:00", uploaded_at: "2026-06-07T09:00:12-04:00",
    highlight: true, ai_tags: ["sample", "amazon", "canoe", "flooded-forest", "wildlife", "remote", "highlight"] },
  { file: "amazon-5.jpg", url: amz5.url,
    title: "Amazon riverboat on the Rio Negro near Manaus",
    caption: "Warm light on the riverboat — Rio Negro near Manaus at golden hour.",
    author: "James Martins",
    license: "CC BY 3.0", licenseUrl: "https://creativecommons.org/licenses/by/3.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Amazon_boat_in_Rio_Negro_River_Manaus_AM)_-_panoramio.jpg",
    attributionText: '"Amazon boat in Rio Negro River, Manaus" by James Martins, CC BY 3.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: -3.1690, lng: -60.1040, altitude_meters: 29, heading_degrees: 310,
    captured_at: "2026-06-07T16:34:44-04:00", uploaded_at: "2026-06-07T16:59:44-04:00",
    highlight: true, ai_tags: ["sample", "amazon", "riverboat", "rio negro", "sunset", "spotty-service", "highlight"] },
  { file: "amazon-6.jpg", url: amz6.url,
    title: "Morning Mist on the Amazon",
    caption: "Soft morning atmosphere before departure — mist over the water at Manaus.",
    author: "Portal da Copa",
    license: "CC BY 3.0 br", licenseUrl: "https://creativecommons.org/licenses/by/3.0/br/",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Port_of_Manaus",
    attributionText: '"Manaus river port" by Portal da Copa, CC BY 3.0 BR (via Wikimedia Commons)',
    attributionRequired: true,
    lat: -3.1008, lng: -60.0253, altitude_meters: 40, heading_degrees: 15,
    captured_at: "2026-06-08T09:18:09-04:00", uploaded_at: "2026-06-08T09:40:09-04:00",
    highlight: false, ai_tags: ["sample", "amazon", "manaus", "mist", "morning", "calm"] },
];

const AMAZON_DAYS: SampleDay[] = [
  { day: 1, title: "Manaus to river lodge", summary: "Port departure, Rio Negro by boat, settle in at the lodge after dark." },
  { day: 2, title: "Canoe and forest walk", summary: "Morning paddle through flooded forest, afternoon canopy boardwalk." },
  { day: 3, title: "Wildlife / river communities / sunset", summary: "Slow river day, riverside community stop, golden-hour return." },
  { day: 4, title: "Return by boat", summary: "Morning mist on the Amazon and the boat back into Manaus." },
];

const AMAZON_HIGHLIGHTS = [
  "Rio Negro by riverboat",
  "Meeting of the Waters",
  "Canoeing the flooded forest",
  "Sunset on the river",
];

/* ============================================================
 * ROCKIES — Canadian Rockies (Banff & Jasper)
 * ============================================================ */

const ROCKIES_ROUTE: SampleRoutePoint[] = [
  { recorded_at: "2026-06-06T09:00:00-06:00", lat: 51.1784, lng: -115.5708, altitude_meters: 1383, heading_degrees: 295, speed_mps: 8.0,  accuracy_meters: 12, label: "Banff townsite — westbound" },
  { recorded_at: "2026-06-06T10:10:00-06:00", lat: 51.3217, lng: -116.1860, altitude_meters: 1731, heading_degrees: 303, speed_mps: 10.5, accuracy_meters: 10, label: "Lake Louise" },
  { recorded_at: "2026-06-06T11:25:00-06:00", lat: 51.3276, lng: -116.1818, altitude_meters: 1735, heading_degrees: 88,  speed_mps: 1.2,  accuracy_meters: 8,  label: "Lake Louise shoreline walk" },
  { recorded_at: "2026-06-07T08:45:00-06:00", lat: 51.4380, lng: -116.1622, altitude_meters: 1884, heading_degrees: 215, speed_mps: 6.5,  accuracy_meters: 11, label: "Moraine Lake road" },
  { recorded_at: "2026-06-07T09:20:00-06:00", lat: 51.3215, lng: -116.1865, altitude_meters: 1885, heading_degrees: 120, speed_mps: 1.0,  accuracy_meters: 8,  label: "Moraine Lake viewpoint" },
  { recorded_at: "2026-06-07T14:30:00-06:00", lat: 51.9301, lng: -116.5765, altitude_meters: 1420, heading_degrees: 320, speed_mps: 14.0, accuracy_meters: 15, label: "Peyto Lake / Icefields Parkway" },
  { recorded_at: "2026-06-08T10:05:00-06:00", lat: 52.2203, lng: -117.2249, altitude_meters: 1980, heading_degrees: 285, speed_mps: 5.5,  accuracy_meters: 13, label: "Columbia Icefield" },
  { recorded_at: "2026-06-08T11:10:00-06:00", lat: 52.1925, lng: -117.2528, altitude_meters: 1995, heading_degrees: 45,  speed_mps: 1.0,  accuracy_meters: 10, label: "Athabasca Glacier pullout" },
  { recorded_at: "2026-06-08T16:45:00-06:00", lat: 52.8734, lng: -118.0814, altitude_meters: 1060, heading_degrees: 20,  speed_mps: 7.5,  accuracy_meters: 12, label: "Jasper townsite — arrival" },
];

const ROCKIES: SamplePhoto[] = [
  { file: "rockies-1.jpg", url: rk1.url,
    title: "Lake Louise Shoreline",
    caption: "Turquoise lake framed by peaks — early morning along the shoreline path.",
    author: "Gorgo",
    license: "Public domain", licenseUrl: "https://en.wikipedia.org/wiki/Public_domain",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Moraine_Lake",
    attributionText: '"Moraine Lake" by Gorgo, Public domain (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 51.4171, lng: -116.2177, altitude_meters: 1731, heading_degrees: 292,
    captured_at: "2026-06-06T10:18:22-06:00", uploaded_at: "2026-06-06T10:37:22-06:00",
    highlight: true, ai_tags: ["sample", "rockies", "lake", "alpine", "banff", "highlight"] },
  { file: "rockies-2.jpg", url: rk2.url,
    title: "Moraine Lake Overlook",
    caption: "High viewpoint above blue water — the iconic Moraine Lake panorama.",
    author: "Vlunyak",
    license: "CC0", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Lake_Louise_(Alberta)",
    attributionText: '"Lake Louise" by Vlunyak, CC0 (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 51.3217, lng: -116.1860, altitude_meters: 1885, heading_degrees: 108,
    captured_at: "2026-06-07T09:22:10-06:00", uploaded_at: "2026-06-07T09:48:10-06:00",
    highlight: true, ai_tags: ["sample", "rockies", "viewpoint", "lake", "mountains", "iconic", "highlight"] },
  { file: "rockies-3.jpg", url: rk3.url,
    title: "Peyto Lake Bend",
    caption: "Sweeping overlook on the Icefields Parkway — Peyto Lake from Bow Summit.",
    author: "dyonis",
    license: "CC BY 2.0", licenseUrl: "https://creativecommons.org/licenses/by/2.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Peyto_Lake",
    attributionText: '"Peyto Lake" by dyonis, CC BY 2.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 51.9301, lng: -116.5765, altitude_meters: 1420, heading_degrees: 320,
    captured_at: "2026-06-07T14:34:45-06:00", uploaded_at: "2026-06-07T14:55:45-06:00",
    highlight: true, ai_tags: ["sample", "rockies", "overlook", "roadtrip", "glacier-fed", "highlight"] },
  { file: "rockies-4.jpg", url: rk4.url,
    title: "Athabasca Glacier Pullout",
    caption: "Wind, ice, and high-elevation landscape — pullout at the Columbia Icefield.",
    author: "ImagePerson",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Athabasca_Glacier",
    attributionText: '"Athabasca Glacier" by ImagePerson, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 52.1925, lng: -117.2528, altitude_meters: 1995, heading_degrees: 45,
    captured_at: "2026-06-08T11:12:02-06:00", uploaded_at: "2026-06-08T11:36:02-06:00",
    highlight: true, ai_tags: ["sample", "rockies", "glacier", "icefield", "altitude", "highlight"] },
  { file: "rockies-5.jpg", url: rk5.url,
    title: "Jasper Evening Walk",
    caption: "Quiet town evening after the drive north — first night in Jasper.",
    author: "Florian Fuchs",
    license: "CC BY 3.0", licenseUrl: "https://creativecommons.org/licenses/by/3.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Icefields_Parkway",
    attributionText: '"Icefields Parkway" by Florian Fuchs, CC BY 3.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 52.8734, lng: -118.0814, altitude_meters: 1060, heading_degrees: 20,
    captured_at: "2026-06-08T17:05:18-06:00", uploaded_at: "2026-06-08T17:25:18-06:00",
    highlight: true, ai_tags: ["sample", "rockies", "town", "evening", "recovery", "calm", "highlight"] },
  { file: "rockies-6.jpg", url: rk6.url,
    title: "Bow River Morning",
    caption: "Soft morning light over the river near Banff — quiet start to day one.",
    author: "Gorgo",
    license: "Public domain", licenseUrl: "https://en.wikipedia.org/wiki/Public_domain",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Banff_National_Park",
    attributionText: '"Banff National Park" by Gorgo, Public domain (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 51.1770, lng: -115.6020, altitude_meters: 1500, heading_degrees: 70,
    captured_at: "2026-06-06T16:15:30-06:00", uploaded_at: "2026-06-06T16:40:30-06:00",
    highlight: false, ai_tags: ["sample", "rockies", "river", "mountains", "morning", "town"] },
];

const ROCKIES_DAYS: SampleDay[] = [
  { day: 1, title: "Arrival in Banff", summary: "Westbound from Calgary, settle into Banff townsite, evening Bow River walk." },
  { day: 2, title: "Lakes and overlooks", summary: "Lake Louise shoreline in the morning, Moraine Lake viewpoint in the afternoon." },
  { day: 3, title: "Icefields Parkway", summary: "Long scenic drive — Peyto Lake, Bow Summit, multiple pullouts." },
  { day: 4, title: "Jasper waterfalls and canyon", summary: "Athabasca and Sunwapta Falls, Maligne Canyon, into Jasper." },
  { day: 5, title: "Slow morning / scenic return", summary: "Easy morning in town, one last overlook on the way out." },
];

const ROCKIES_HIGHLIGHTS = [
  "Lake Louise shoreline",
  "Moraine Lake overlook",
  "Peyto Lake on the Icefields Parkway",
  "Athabasca Glacier pullout",
  "Jasper evening walk",
];

/* ============================================================
 * JAPAN — Tokyo to Kyoto
 * ============================================================ */

const JAPAN_ROUTE: SampleRoutePoint[] = [
  { recorded_at: "2026-06-06T09:10:00+09:00", lat: 35.6812, lng: 139.7671, altitude_meters: 12, heading_degrees: 185, speed_mps: 1.4,  accuracy_meters: 10, label: "Tokyo — Shibuya / Harajuku" },
  { recorded_at: "2026-06-06T10:05:00+09:00", lat: 35.6586, lng: 139.7454, altitude_meters: 28, heading_degrees: 210, speed_mps: 1.0,  accuracy_meters: 8,  label: "Tokyo — Asakusa / Sumida" },
  { recorded_at: "2026-06-07T08:45:00+09:00", lat: 35.6812, lng: 139.7671, altitude_meters: 12, heading_degrees: 250, speed_mps: 2.0,  accuracy_meters: 10, label: "Tokyo Station — departure" },
  { recorded_at: "2026-06-07T10:30:00+09:00", lat: 35.1032, lng: 138.8599, altitude_meters: 10, heading_degrees: 260, speed_mps: 76.0, accuracy_meters: 50, label: "Tokaido Shinkansen — en route" },
  { recorded_at: "2026-06-07T11:05:00+09:00", lat: 34.9858, lng: 135.7588, altitude_meters: 34, heading_degrees: 300, speed_mps: 1.8,  accuracy_meters: 10, label: "Kyoto Station — arrival" },
  { recorded_at: "2026-06-07T15:15:00+09:00", lat: 34.9671, lng: 135.7727, altitude_meters: 58, heading_degrees: 90,  speed_mps: 1.0,  accuracy_meters: 7,  label: "Fushimi Inari" },
  { recorded_at: "2026-06-08T09:20:00+09:00", lat: 35.0094, lng: 135.6668, altitude_meters: 46, heading_degrees: 330, speed_mps: 0.8,  accuracy_meters: 7,  label: "Arashiyama Bamboo Grove" },
  { recorded_at: "2026-06-08T14:00:00+09:00", lat: 35.0394, lng: 135.7292, altitude_meters: 96, heading_degrees: 45,  speed_mps: 1.0,  accuracy_meters: 8,  label: "Gion evening walk" },
];

const JAPAN: SamplePhoto[] = [
  { file: "japan-1.jpg", url: jp1.url,
    title: "Shibuya Crossing Glow",
    caption: "Evening city motion and bright signage — first night in Tokyo.",
    author: "David Kernan",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Shibuya_Crossing",
    attributionText: '"Shibuya Crossing" by David Kernan, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 35.6812, lng: 139.7671, altitude_meters: 12, heading_degrees: 185,
    captured_at: "2026-06-06T09:14:33+09:00", uploaded_at: "2026-06-06T09:35:33+09:00",
    highlight: true, ai_tags: ["sample", "japan", "tokyo", "city", "night", "crossing", "highlight"] },
  { file: "japan-2.jpg", url: jp2.url,
    title: "Asakusa Lanterns",
    caption: "Temple approach with traditional storefronts — afternoon stroll in Asakusa.",
    author: "Balon Greyjoy",
    license: "CC0", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Fushimi_Inari-taisha",
    attributionText: '"Fushimi Inari shrine" by Balon Greyjoy, CC0 (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 35.6586, lng: 139.7454, altitude_meters: 28, heading_degrees: 210,
    captured_at: "2026-06-06T10:08:10+09:00", uploaded_at: "2026-06-06T10:30:10+09:00",
    highlight: false, ai_tags: ["sample", "japan", "tokyo", "temple", "tradition", "street"] },
  { file: "japan-3.jpg", url: jp3.url,
    title: "Platform Before Kyoto",
    caption: "Bags packed, train moment, transition day — boarding the Shinkansen.",
    author: "Mitchwandrew",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Arashiyama_Bamboo_Forest",
    attributionText: '"Arashiyama bamboo grove" by Mitchwandrew, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 34.9858, lng: 135.7588, altitude_meters: 34, heading_degrees: 300,
    captured_at: "2026-06-07T11:07:41+09:00", uploaded_at: "2026-06-07T11:26:41+09:00",
    highlight: true, ai_tags: ["sample", "japan", "shinkansen", "train", "travel-day", "highlight"] },
  { file: "japan-4.jpg", url: jp4.url,
    title: "Fushimi Inari Steps",
    caption: "Red gates climbing into the trees — the torii path at Fushimi Inari.",
    author: "Jakub Hałun",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Kinkaku-ji",
    attributionText: '"Kinkaku-ji" by Jakub Hałun, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 34.9671, lng: 135.7727, altitude_meters: 58, heading_degrees: 90,
    captured_at: "2026-06-07T15:18:09+09:00", uploaded_at: "2026-06-07T15:41:09+09:00",
    highlight: true, ai_tags: ["sample", "japan", "kyoto", "shrine", "torii", "hike", "highlight"] },
  { file: "japan-5.jpg", url: jp5.url,
    title: "Arashiyama Bamboo",
    caption: "Shaded green path through tall bamboo — morning at Arashiyama.",
    author: "David Kernan",
    license: "CC BY 4.0", licenseUrl: "https://creativecommons.org/licenses/by/4.0",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Tokyo_Tower",
    attributionText: '"Tokyo Tower at night" by David Kernan, CC BY 4.0 (via Wikimedia Commons)',
    attributionRequired: true,
    lat: 35.0094, lng: 135.6668, altitude_meters: 46, heading_degrees: 330,
    captured_at: "2026-06-08T09:22:58+09:00", uploaded_at: "2026-06-08T09:44:58+09:00",
    highlight: true, ai_tags: ["sample", "japan", "kyoto", "bamboo", "quiet", "highlight"] },
  { file: "japan-6.jpg", url: jp6.url,
    title: "Gion Evening Walk",
    caption: "Lanterns and narrow streets at dusk — last evening in Kyoto.",
    author: "Gary Lee Todd, Ph.D.",
    license: "CC0", licenseUrl: "https://creativecommons.org/publicdomain/zero/1.0/",
    sourceUrl: "https://commons.wikimedia.org/wiki/Category:Shinkansen",
    attributionText: '"Shinkansen" by Gary Lee Todd, CC0 (via Wikimedia Commons)',
    attributionRequired: false,
    lat: 35.0394, lng: 135.7292, altitude_meters: 96, heading_degrees: 45,
    captured_at: "2026-06-08T14:04:22+09:00", uploaded_at: "2026-06-08T14:26:22+09:00",
    highlight: false, ai_tags: ["sample", "japan", "kyoto", "evening", "old-town", "walk"] },
];

const JAPAN_DAYS: SampleDay[] = [
  { day: 1, title: "Tokyo arrival and first night", summary: "Settle in, evening through Shibuya and Harajuku." },
  { day: 2, title: "Tokyo neighborhoods", summary: "Asakusa temple approach, Sumida River, into Tokyo Station." },
  { day: 3, title: "Train to Kyoto", summary: "Shinkansen south, arrive Kyoto, afternoon at Fushimi Inari." },
  { day: 4, title: "Kyoto temples and old streets", summary: "Higashiyama, Kiyomizu area, Philosopher's Path." },
  { day: 5, title: "Bamboo grove / river / evening walk", summary: "Arashiyama in the morning, slow afternoon, Gion at dusk." },
];

const JAPAN_HIGHLIGHTS = [
  "Shibuya Crossing at night",
  "Shinkansen to Kyoto",
  "Fushimi Inari torii path",
  "Arashiyama bamboo grove",
];

/* ============================================================ */

export const SAMPLE_PRESETS: Record<SamplePresetKey, SamplePreset> = {
  alps: {
    key: "alps",
    name: "Italian Alps — Dolomites trek",
    shortName: "Italian Alps",
    tagline: "Refuges, Tre Cime, and turquoise lakes",
    subtitle: "Hut-to-hut hiking through the Dolomites — Tre Cime, Cadini, and a rifugio sunset.",
    description:
      "Five-day alpine traverse out of Cortina d'Ampezzo with high-altitude switchbacks, jagged peaks, " +
      "and quiet alpine lakes. Strong route-elevation feel and clustered photo pods make this the best " +
      "sample for showing off map + route + highlight grouping.",
    destination: "Dolomites, Italy",
    heroUrl: ALPS[3].url, // Tre Cime
    timezone: "Europe/Rome",
    dateStart: "2026-06-06",
    dateEnd: "2026-06-08",
    route: ALPS_ROUTE,
    photos: ALPS,
    days: ALPS_DAYS,
    highlights: ALPS_HIGHLIGHTS,
  },
  amazon: {
    key: "amazon",
    name: "Brazilian rainforest — Amazon",
    shortName: "Amazon",
    tagline: "River boats, canopy, and the meeting of waters",
    subtitle: "Riverboat days on the Rio Negro — flooded forest, sunset returns, spotty-service stretches.",
    description:
      "Four days based out of Manaus with long river segments, a flooded-forest canoe morning, and a " +
      "golden-hour return. Sparse GPS points and offline-friendly tags make this the best sample for " +
      "showing the queue + offline storytelling.",
    destination: "Amazonas, Brazil",
    heroUrl: AMAZON[2].url, // Meeting of Waters
    timezone: "America/Manaus",
    dateStart: "2026-06-06",
    dateEnd: "2026-06-08",
    route: AMAZON_ROUTE,
    photos: AMAZON,
    days: AMAZON_DAYS,
    highlights: AMAZON_HIGHLIGHTS,
  },
  rockies: {
    key: "rockies",
    name: "Canadian Rockies — Banff & Jasper",
    shortName: "Canadian Rockies",
    tagline: "Moraine, Lake Louise, Icefields Parkway",
    subtitle: "Banff to Jasper on the Icefields Parkway — glacial lakes, mountain pullouts, alpine air.",
    description:
      "Five-day mountain road trip from Calgary through Banff, Lake Louise, and Moraine, north on the " +
      "Icefields Parkway past Peyto and the Columbia Icefield, ending with quiet Jasper evenings. " +
      "Mixes long driving segments with short scenic walks.",
    destination: "Alberta, Canada",
    heroUrl: ROCKIES[1].url, // Moraine
    timezone: "America/Edmonton",
    dateStart: "2026-06-06",
    dateEnd: "2026-06-08",
    route: ROCKIES_ROUTE,
    photos: ROCKIES,
    days: ROCKIES_DAYS,
    highlights: ROCKIES_HIGHLIGHTS,
  },
  japan: {
    key: "japan",
    name: "Japan — Tokyo to Kyoto",
    shortName: "Japan",
    tagline: "Shibuya nights, shinkansen, Fushimi torii",
    subtitle: "Tokyo speed to Kyoto calm — Shibuya night, Shinkansen south, torii at Fushimi Inari.",
    description:
      "Five-day city-and-train trip — two nights in Tokyo, a Shinkansen transition day, then three " +
      "slower days in Kyoto across Fushimi Inari, Arashiyama, and a final Gion evening walk. " +
      "Great sample for mixed-tempo route storytelling.",
    destination: "Honshu, Japan",
    heroUrl: JAPAN[3].url, // Fushimi
    timezone: "Asia/Tokyo",
    dateStart: "2026-06-06",
    dateEnd: "2026-06-08",
    route: JAPAN_ROUTE,
    photos: JAPAN,
    days: JAPAN_DAYS,
    highlights: JAPAN_HIGHLIGHTS,
  },
};

export const SAMPLE_PRESETS_LIST: readonly SamplePreset[] = SAMPLE_PRESET_KEYS.map(
  (k) => SAMPLE_PRESETS[k],
);

export function isSamplePresetKey(v: unknown): v is SamplePresetKey {
  return typeof v === "string" && (SAMPLE_PRESET_KEYS as readonly string[]).includes(v);
}

/**
 * Resolve a "sample://<preset>/<file>" storage_path to a CDN URL.
 * Returns null if the path isn't a sample path or the file isn't known.
 */
export function resolveSampleMediaUrl(storagePath: string | null | undefined): string | null {
  if (!storagePath) return null;
  const m = /^sample:\/\/([^/]+)\/(.+)$/.exec(storagePath);
  if (!m) return null;
  const preset = m[1] as SamplePresetKey;
  const file = m[2];
  if (!isSamplePresetKey(preset)) return null;
  const photo = SAMPLE_PRESETS[preset].photos.find((p) => p.file === file);
  return photo?.url ?? null;
}
